import { ok, type Result } from 'neverthrow';
import type {
  ApiErr,
  GetStackFramesForRunKeyPreviewResponse,
  GetStackFramesForRunKeyResponse,
} from '../etc/api/typed-client';
import { Semaphore } from '../etc/semaphore';

/** Nest may respond with an empty body when the handler returns null. */
const normalizeStackFrameResponse = <T>(
  result: Result<T | null, ApiErr>
): Result<T | null, ApiErr> => {
  if (result.isErr()) {
    return result;
  }
  const value = result.value;
  if (value === null || value === undefined || value === '') {
    return ok(null);
  }
  return result;
};

/** Limits concurrent stack-frame HTTP requests across the app. */
export const stackFrameRequestRateLimiter = new Semaphore(50);

/** Shared TTL — matches AtcCellModel poll interval so staggered pollers reuse one HTTP result. */
export const STACK_FRAME_RECENT_RESULT_TTL_MS = 4000;

const inFlightRequests = new Map<string, Promise<unknown>>();
const recentResults = new Map<string, { value: unknown; timestamp: number }>();

const cacheKey = (kind: 'preview' | 'full', runKey: string): string =>
  `${kind}:${runKey}`;

const getRecentResult = <T>(key: string): T | undefined => {
  const entry = recentResults.get(key);
  if (entry === undefined) {
    return undefined;
  }
  if (Date.now() - entry.timestamp >= STACK_FRAME_RECENT_RESULT_TTL_MS) {
    recentResults.delete(key);
    return undefined;
  }
  return entry.value as T;
};

const setRecentResult = (key: string, value: unknown): void => {
  recentResults.set(key, { value, timestamp: Date.now() });
};

const dedupeInFlight = <T>(key: string, fn: () => Promise<T>): Promise<T> => {
  const existing = inFlightRequests.get(key);
  if (existing !== undefined) {
    return existing as Promise<T>;
  }

  const promise = fn().finally(() => {
    if (inFlightRequests.get(key) === promise) {
      inFlightRequests.delete(key);
    }
  });
  inFlightRequests.set(key, promise);
  return promise;
};

type StackFrameAtcClient = {
  getStackFramesForRunKey: (p: {
    runKey: string;
  }) => Promise<Result<GetStackFramesForRunKeyResponse | null, ApiErr>>;
  getStackFramesForRunKeyPreview: (p: {
    runKey: string;
  }) => Promise<Result<GetStackFramesForRunKeyPreviewResponse | null, ApiErr>>;
};

const fetchWithRecentCache = <T>(
  key: string,
  fetchFn: () => Promise<T>
): Promise<T> => {
  const recent = getRecentResult<T>(key);
  if (recent !== undefined) {
    return Promise.resolve(recent);
  }

  return dedupeInFlight(key, async () => {
    const recentInside = getRecentResult<T>(key);
    if (recentInside !== undefined) {
      return recentInside;
    }

    await stackFrameRequestRateLimiter.acquire();
    try {
      const result = await fetchFn();
      setRecentResult(key, result);
      return result;
    } finally {
      await stackFrameRequestRateLimiter.release();
    }
  });
};

export const fetchStackFramesForRunKey = (
  client: StackFrameAtcClient,
  runKey: string
): Promise<Result<GetStackFramesForRunKeyResponse | null, ApiErr>> => {
  return fetchWithRecentCache(cacheKey('full', runKey), async () =>
    normalizeStackFrameResponse(
      await client.getStackFramesForRunKey({ runKey })
    )
  );
};

export const fetchStackFramesPreviewForRunKey = (
  client: StackFrameAtcClient,
  runKey: string
): Promise<Result<GetStackFramesForRunKeyPreviewResponse | null, ApiErr>> => {
  return fetchWithRecentCache(cacheKey('preview', runKey), async () =>
    normalizeStackFrameResponse(
      await client.getStackFramesForRunKeyPreview({ runKey })
    )
  );
};

/** Bust cached + in-flight state when a cell run is (re)started. */
export const invalidateStackFrameFetchCacheForRunKey = (
  runKey: string
): void => {
  recentResults.delete(cacheKey('preview', runKey));
  recentResults.delete(cacheKey('full', runKey));
  inFlightRequests.delete(cacheKey('preview', runKey));
  inFlightRequests.delete(cacheKey('full', runKey));
};

/** Clears fetch caches — for tests only. */
export const clearStackFrameInFlightRequestsForTests = (): void => {
  inFlightRequests.clear();
  recentResults.clear();
};
